/**
 * Keep PostHog attribution intact across trips through sites we don't control.
 *
 * The console sends visitors away and back several times: Auth0 (and Google /
 * GitHub social login) to sign in, Stripe for checkout and the billing portal,
 * GitHub to install the app. The distinct_id and session survive these trips on
 * their own, because the PostHog cookie is first-party on .testdriver.ai and
 * the browser keeps it. The referrer does not: the page load that comes back
 * has `document.referrer` set to the partner, so every event on it would credit
 * accounts.google.com, checkout.stripe.com or github.com as the visit's source.
 *
 * This follows PostHog's cross-domain guide ("Fix the referrer and session
 * reset"): on a partner return, set `$referrer` to the console page the visitor
 * left from instead.
 */

const LAST_URL_KEY = "td_last_own_url";

// Hosts that only send a visitor to the console because we sent them there.
// github.com is NOT here: links in PR comments and READMEs are real referrals,
// so a GitHub return is recognized by its landing URL instead (see below).
const PARTNER_HOSTS = [
  /(^|\.)auth0\.com$/,
  /^accounts\.google\.com$/,
  /(^|\.)stripe\.com$/,
];

/** Capture options for an event fired right before the browser leaves. */
export const BEFORE_REDIRECT = { transport: "sendBeacon", send_instantly: true };

function hostOf(url) {
  try {
    return new URL(url).hostname;
  } catch (_e) {
    return "";
  }
}

// Decided once per page load: document.referrer never changes in an SPA, so
// every event on this load carries the same (partner) $referrer.
const returnedFromPartner = (() => {
  if (typeof window === "undefined") return false;
  const host = hostOf(document.referrer);
  if (!host || host === window.location.hostname) return false;
  if (PARTNER_HOSTS.some((re) => re.test(host))) return true;

  // Landing URLs only a partner redirect produces: the Auth0 callback (covers
  // GitHub social login) and the GitHub App install return.
  const { pathname, search } = window.location;
  const query = new URLSearchParams(search);
  return (
    pathname === "/auth0-callback" ||
    (host === "github.com" && (query.has("installation_id") || query.has("setup_action")))
  );
})();

const partnerReferrer = returnedFromPartner ? document.referrer : null;

function readLastOwnUrl() {
  try {
    return sessionStorage.getItem(LAST_URL_KEY);
  } catch (_e) {
    return null;
  }
}

// The console page the visitor left from, read once before this load
// overwrites it. sessionStorage is per tab and survives the same-tab redirect.
const departureUrl = returnedFromPartner ? readLastOwnUrl() : null;

/** Remember the current console URL, so a partner return can point back to it. */
export function recordOwnUrl() {
  try {
    sessionStorage.setItem(LAST_URL_KEY, window.location.href);
  } catch (_e) {
    /* storage unavailable; the return falls back to $direct */
  }
}

/**
 * PostHog `before_send` step: rewrite a partner referrer to the departure page,
 * or `$direct` when we don't know where they left from.
 */
export function fixPartnerReferrer(event) {
  if (!returnedFromPartner || !event?.properties) return event;

  const referrer = departureUrl || "$direct";
  const domain = departureUrl ? hostOf(departureUrl) : "$direct";
  const props = event.properties;

  if (props.$referrer === partnerReferrer || PARTNER_HOSTS.some((re) => re.test(props.$referring_domain || ""))) {
    props.$referrer = referrer;
    props.$referring_domain = domain;
  }

  // First-touch person properties, for a visitor whose first-ever event is this
  // return. A partner is never how someone found us.
  const once = event.$set_once || props.$set_once;
  if (once && hostOf(once.$initial_referrer || "") === hostOf(partnerReferrer)) {
    once.$initial_referrer = referrer;
    once.$initial_referring_domain = domain;
  }

  return event;
}
